import { get, head, post, getCount, getPage, buildOptions, buildWhereFilter } from 'stores/_services/dataService';
import { getRuleVersion } from './ruleService';
import { getConditions } from 'stores/_services/filterService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';

export { findById, archive, unarchive, getAlert, getAlertSummary, getActiveAlertCount, exportAlertCsv, archiveAlertsByQuery, unarchiveAlertsByQuery };

const route = '/alertDetails';
const SEARCH_FIELDS = ['search'];

async function findById(alertId) {
    let options = {
        filter: {
            where: { id: alertId },
            include: [
                {
                    relation: 'response',
                    scope: {
                        order: 'createdOn asc',
                        fields: ['extensionId'],
                        include: {
                            relation: 'extension',
                            scope: {
                                fields: ['name']
                            }
                        }
                    }
                }
            ]
        }
    };

    const alertDetailResponse = await get(`${route}`, options);
    const alertDetails = alertDetailResponse.data;
    let alertDetail = alertDetails[0];

    if (alertDetail.sourceType === 'rule' || alertDetail.sourceType === 'correlation') {
        const ruleVersionResponse = await getRuleVersion(alertDetail.sourceVersionId);
        const versionMetaData = {
            author: ruleVersionResponse.author,
            createdOn: ruleVersionResponse.createdOn,
            body: ruleVersionResponse.body
        };

        alertDetail.source = ruleVersionResponse;

        if (!ruleVersionResponse.name) {
            const ruleResponse = await get(`/rules/${alertDetail.sourceId}`);

            alertDetail.source = ruleResponse.data;
            //Read rule name from alert detail that produced the event
            alertDetail.source.name = alertDetail.sourceName;
        }

        alertDetail.source.version = versionMetaData;
    }

    if (
        (alertDetail.sourceType === 'rule' || alertDetail.sourceType === 'reputation' || alertDetail.sourceType === 'correlation') &&
        !alertDetail.source?.recommendation
    ) {
        let recommendation;

        if (!alertDetail.source) alertDetail.source = {};

        if (alertDetail.sourceType === 'correlation') {
            recommendation = { Identification: ['See attached correlated alerts'], Containment: [], Eradication: [] };
        } else {
            const filter = { where: { alertType: alertDetail.sourceType, itemType: alertDetail.type }, includeFields: ['recommendation'] };

            const { data } = await get('/alertrecommendations', { filter });

            recommendation = data['0']?.recommendation;
        }

        alertDetail.source.recommendation = recommendation;
    }

    if (alertDetail.sourceType === 'extension') {
        const options = {
            filter: {
                where: {
                    id: alertDetail.sourceId
                },
                include: [
                    {
                        relation: 'editor',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const extensionResponse = await get('/extensions', options);
        alertDetail.source = extensionResponse.data[0];

        const extensionVersionResponse = await get(`/extensionversions/${alertDetail.sourceVersionId}/detail`);
        alertDetail.source.version = extensionVersionResponse.data;
    }

    const result = alertDetail;

    return result;
}

async function archive(id) {
    let options = {
        queryParams: { where: { id } }
    };

    try {
        await post('alerts/archive', null, options);
    } catch (e) {
        console.log(e.message);
    }
}

async function unarchive(id) {
    let options = {
        queryParams: { where: { id } }
    };

    try {
        await post('alerts/unarchive', null, options);
    } catch (e) {
        console.log(e.message);
    }
}

async function getAlertSummary(param) {
    const { limit, skip, sortBy, sortDirection, filter, fields } = param;

    const sort = `${sortBy} ${sortDirection}`;

    const conditions = getConditions(filter);

    const options = {
        filter: {
            limit,
            skip,
            fields,
            searchFields: SEARCH_FIELDS,
            where: conditions,
            order: sort
        }
    };

    let { data, count } = await getPage('alertDetails', options);

    return {
        items: data,
        count: count
    };
}

async function getAlert(param) {
    const { limit, skip, sortBy, sortDirection, filter, fields } = param;

    const sort = `${sortBy} ${sortDirection}`;

    const emptyResults = { items: [], count: 0 };

    let dateValidation = await validateDateBetweenFilter(filter.dateBetween);

    if (!dateValidation) return emptyResults;

    const conditions = getConditions(filter);

    const options = {
        filter: {
            limit,
            skip,
            fields,
            searchFields: SEARCH_FIELDS,
            where: conditions,
            order: sort,
            include: [
                {
                    relation: 'response',
                    scope: {
                        order: 'createdOn asc',
                        fields: ['extensionId'],
                        include: {
                            relation: 'extension',
                            scope: {
                                fields: ['name']
                            }
                        }
                    }
                },
                {
                    relation: 'comments',
                    fields: ['id']
                }
            ]
        }
    };

    let { data, count } = await getPage('alertDetails', options);

    return {
        items: data,
        count: count
    };
}

async function exportAlertCsv(conditions, sortBy, sortDirection) {
    let options = {
        filter: {
            searchFields: SEARCH_FIELDS,
            where: conditions,
            order: `${sortBy} ${sortDirection}`
        }
    };

    let query = buildOptions(options);

    window.location = await buildCsvDownloadUrl('alertDetails', query);
}

async function getActiveAlertCount() {
    //Fetch count of only alerts
    return await getCount('alerts', { where: { archived: false, signal: true } });
}

async function archiveAlertsByQuery(conditions) {
    const where = buildWhereFilter({
        where: conditions,
        searchFields: SEARCH_FIELDS
    });

    const options = {
        queryParams: { where }
    };

    return await post('alerts/archive', null, options);
}

async function unarchiveAlertsByQuery(conditions) {
    const where = buildWhereFilter({
        where: conditions,
        searchFields: SEARCH_FIELDS
    });

    const options = {
        queryParams: { where }
    };

    return await post('alerts/unarchive', null, options);
}

async function validateDateBetweenFilter(dateBetween) {
    if (!dateBetween || (!dateBetween.from && !dateBetween.to)) {
        return true;
    }

    if ((dateBetween.from && !dateBetween.to) || (dateBetween.to && !dateBetween.from)) {
        return true;
    }

    if (dateBetween.from == 'Invalid date' || dateBetween.to == 'Invalid date') {
        return false;
    }

    return true;
}
